import { useEffect } from "react";

import moment from "moment";

import { clsx } from "@alpacahq/ui/old";

import { useGetMarketStatus } from "~/v2/api/hooks/useGetMarketStatus";
import type { Asset } from "~/v2/api/rest";
import type { Quote } from "~/v2/api/rest/data";
import {
  type AssetTradingStatus,
  AssetTradingStatusBadge,
} from "~/v2/components/AssetTradingStatusBadge";
import { getPrettyAmount } from "~/v2/utils/formatting";

function getAssetTradingStatus(
  asset: Asset,
  isOvernight: boolean,
): AssetTradingStatus | null {
  if (!asset.tradable) {
    return "not_tradable";
  }

  if (!isOvernight || asset.class === "crypto") {
    return null;
  }

  if (!asset.attributes?.includes("overnight_tradable")) {
    return "overnight_not_supported";
  }

  if (asset.attributes?.includes("overnight_halted")) {
    return "overnight_halted";
  }

  return null;
}

interface HeroProps {
  asset: Asset;
  quote: Quote | undefined;
}

export const Hero = ({ asset, quote }: HeroProps) => {
  const hasQuotePrice = quote?.ask !== undefined && quote?.bid !== undefined;
  const hasChangeData =
    quote?.last !== undefined &&
    quote?.last_day_closing !== undefined &&
    quote.last_day_closing !== "0";

  const price = hasQuotePrice
    ? getPrettyAmount((quote.ask + quote.bid) / 2, 0, 8)
    : undefined;

  const last = quote?.last ?? 0;
  const lastDayClosing = parseFloat(quote?.last_day_closing ?? "0");
  const change = last - lastDayClosing;
  const changePercent =
    lastDayClosing !== 0 ? (change / lastDayClosing) * 100 : 0;

  const isGain = change >= 0;
  const color = isGain ? "text-green-500" : "text-rose-500";
  const chevron = isGain ? "▲" : "▼";

  useEffect(() => {
    if (price && asset?.symbol) {
      document.title = `Alpaca — ${asset.symbol} ${price}`;
    }
  }, [price, asset?.symbol]);

  const { marketStatus } = useGetMarketStatus();
  const isOvernight = marketStatus?.phase === "overnight";

  const tradingStatus = getAssetTradingStatus(asset, isOvernight);

  return (
    <>
      <div className="flex w-full justify-between">
        <div className="flex items-end justify-start gap-2">
          <h2 className="m-0 font-semibold text-xl leading-none tracking-tight">
            {asset?.symbol || ""}
          </h2>
          <h3 className="m-0 text-base text-zinc-500">{asset?.name}</h3>
        </div>
      </div>
      {tradingStatus && (
        <div className="mt-2">
          <AssetTradingStatusBadge status={tradingStatus} />
        </div>
      )}
      <div className="mt-3 mb-6 grid gap-3">
        <h1 className="flex items-start gap-2 font-bold text-xl leading-none tracking-tighter">
          {price ?? "—"}
          {hasChangeData && (
            <div
              className={clsx("font-medium text-lg tracking-tighter", color)}
            >
              <span className="text-sm">{chevron}</span>{" "}
              {getPrettyAmount(change, 0, 8)} ({changePercent.toFixed(2)}%)
            </div>
          )}
        </h1>
        {quote?.bid_timestamp ? (
          <h4 className="text-sm text-zinc-500">
            Updated {moment(quote.bid_timestamp).fromNow()}.
          </h4>
        ) : (
          <h4 className="text-sm text-zinc-400">Market data unavailable</h4>
        )}
      </div>
    </>
  );
};
